import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import EventCard from '../EventCard'
import { Link } from 'react-router-dom';
import Button from '../Button/Button';
import EventMap from '../EventMap';
import Banner from '../Banner';
import Typography from '../../components/Typography';
import { theme } from '../../theme';
import { useEventContext } from '../../context/EventContext';
import ArrowRightUpIcon from '../Icons/ArrowRightUpIcon';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';
import LocationIcon from '../Icons/LocationIcon';
import TicketSolidIcon from '../Icons/TicketSolidIcon';
import AttendeesIcon from '../Icons/AttendeesIcon';
import EditSolidIcon from '../Icons/EditSolidIcon';
import AddTicketSolidIcon from '../Icons/AddTicketSolidIcon';
import { isEventPast, formatEventDateRange, formatMonthDate, formatDayDate } from '../../utils/dateUtils';
import IncognitoIcon from '../Icons/IncognitoIcon';
import PastEventBanner from '../EventSummary/PastEventBanner';
import { TICKET_PAID } from '../../constants/eventTypes';


const ManageEvent = () => {
  const { eventDetails, updateEventDetails, isEventPublished } = useEventContext();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('manageEventSummary');

  // Si eventDetails aún no está definido, mostramos un mensaje de carga
  if (!eventDetails) {
    return <p>{t('loading')}</p>;
  }

  const {
    id,
    title,
    location,
    locationCoordinates,
    shortLocation,
    startDate,
    endDate,
    published,
    tickets,
    attendees,
    attendeesCount,
  } = eventDetails;

  // Format event date range according to requirements
  const eventDateRange = formatEventDateRange(startDate, endDate, i18n.language);
  const formattedMonthDate = formatMonthDate(startDate, i18n.language);
  const formattedDayDate = formatDayDate(startDate);

  const handleEditEvent = () => {
    if (isEventPast(endDate)) {
      toast.error('No puedes editar un evento que ya ha finalizado.');
      return;
    }
    navigate(`/events/manage/${eventDetails.id}/edit`);
  };

  const handleEditTickets = () => {
    if (isEventPast(endDate)) {
      toast.error('No puedes editar los tickets de un evento que ya ha finalizado.');
      return;
    }
    navigate(`/events/manage/${eventDetails.id}/registration`);
  };

  // Verificar si las coordenadas existen y son válidas
  const hasValidCoordinates =
    locationCoordinates &&
    Array.isArray(locationCoordinates.coordinates) &&
    locationCoordinates.coordinates.length === 2 &&
    typeof locationCoordinates.coordinates[0] === 'number' &&
    typeof locationCoordinates.coordinates[1] === 'number';

  // Extraer lat y lng correctamente
  const [lng, lat] = hasValidCoordinates ? locationCoordinates.coordinates : [undefined, undefined];

  const handlePublish = async () => {
    try {
      const response = await axiosClient.post(`/api/events/${id}/publish`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Actualizar el estado localmente
        updateEventDetails(response.data.event);
      }
    } catch (error) {
      console.error('Error al publicar el evento:', error);
      toast.error(t('errors.publishError'));
    }
  };

  // Manejar el caso donde tickets o attendees puedan ser undefined
  const ticketsArray = Array.isArray(tickets) ? tickets : [];
  const attendeesArray = Array.isArray(attendees) ? attendees : [];

  // Calcular el número de inscritos por ticket
  const attendeesPerTicket = {};

  // Inicializar el conteo por ticket
  ticketsArray.forEach(ticket => {
    attendeesPerTicket[ticket._id] = {
      name: ticket.name,
      count: 0,
    };
  });

  // Contar asistentes por ticket (solo los que están 'attending' o 'confirmation_pending')
  attendeesArray.forEach(attendee => {
    // Verificar si el attendee tiene ticketId y está en estado válido
    if (attendee.ticketId && (attendee.status === 'attending' || attendee.status === 'confirmation_pending')) {
      const ticketId = typeof attendee.ticketId === 'object' ? attendee.ticketId._id : attendee.ticketId;
      if (attendeesPerTicket[ticketId]) {
        attendeesPerTicket[ticketId].count += 1;
      }
    }
  });

  return (
    <ManageEventContainer>
      {!published && !isEventPublished && eventDetails?.status !== 'cancelled' && !isEventPast(endDate) && (
        <BannerWrapper>
          {/* <Banner> */}
          <BannerPublishContent>
            <BannerIconWrapper>
              <IncognitoIcon height={32} width={32} fill={theme.colors.defaultStrong} />
              {/* <InfoCircleOutlineIcon fill={theme.colors.defaultStrong} /> */}
            </BannerIconWrapper>
            <BannerContent>
              <BannerData>
                <Typography as="h3" $variant="title-5-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold">
                  {t('privateMode.title')}
                </Typography>
                <Typography as="p" $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                  {t('privateMode.description')}
                </Typography>
              </BannerData>
              <BannerActions>
                <Button
                  onClick={handlePublish}
                  size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                >
                  {t('privateMode.makePublic')}
                </Button>
              </BannerActions>
            </BannerContent>
          </BannerPublishContent>
          {/* </Banner> */}
        </BannerWrapper>
      )}
      <Container>
        <EventSummaryContainer>
          <EventCardWrapper>
            <EventCard maxWidth="320px" event={eventDetails} clickable={false} />
            <Button $variant="ghost" as={Link} to={`/events/${id}/${title}`}>
              {t('actions.viewEventPage')}
              <ArrowRightUpIcon fill={theme.colors.defaultStrong} />
            </Button>
          </EventCardWrapper>
          <EventCover src={eventDetails.image} alt={eventDetails.title} />
          <EventDetailsWrapper>
            {eventDetails?.status !== 'cancelled' && !isEventPast(endDate) && (
              <EventDetailActions>
                <Button
                  $variant="outline"
                  size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                  onClick={handleEditEvent}
                  disabled={isEventPast(endDate) || eventDetails?.status === 'cancelled'}
                  $fullWidth
                  style={{ flexShrink: '1' }}

                >
                  <EditSolidIcon height={20} width={20} fill={theme.colors.defaultStrong} />
                  {t('actions.editEvent')}
                </Button>
                <Button
                  $variant="outline"
                  size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                  onClick={handleEditTickets}
                  disabled={isEventPast(endDate) || eventDetails?.status === 'cancelled'}
                  $fullWidth
                  style={{ flexShrink: '1' }}
                >
                  <AddTicketSolidIcon height={20} width={20} fill={theme.colors.defaultStrong} />
                  {t('actions.editTickets')}
                </Button>
              </EventDetailActions>
            )}
            {isEventPast(endDate) && (
              <PastEventBanner eventEndDate={endDate} borderRadius="0px" width='100%' borderLeft='0px' borderRight='0px' borderTop='0px' borderBottom='1px' borderColor={theme.border.defaultSubtle} />
            )}
            <EventDetailRow>
              <DateContainer>
                <MonthDate>
                  <Typography $variant="overline-semibold" $textTransform="uppercase" color={theme.colors.defaultStrong} as="p">
                    {formattedMonthDate}
                  </Typography>
                </MonthDate>
                <DayDate>
                  <Typography $variant="body-1-bold" color={theme.colors.defaultStrong} as="p">
                    {formattedDayDate}
                  </Typography>
                </DayDate>
              </DateContainer>
              <DataWrapper>
                {eventDateRange.singleLine ? (
                  <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong} as="p">
                    {eventDateRange.singleLine}
                  </Typography>
                ) : (
                  <>
                    <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong} as="p">
                      {eventDateRange.startLine}
                    </Typography>
                    <Typography $variant="body-2-semibold" color={theme.colors.defaultWeak} as="p" style={{ marginBottom: theme.sizing.sm }}>
                      {eventDateRange.endLine}
                    </Typography>
                  </>
                )}
              </DataWrapper>
            </EventDetailRow>
            <EventDetailRow>
              <RowContainer>
                <LocationIcon fill={theme.colors.defaultStrong} height={24} width={24} />
              </RowContainer>
              <DataWrapper>
                <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong} as="p">
                  {location}
                </Typography>
                <Typography $variant="body-2-medium" color={theme.colors.defaultWeak} as="p" style={{ marginBottom: theme.sizing.xs }}>
                  {shortLocation}
                </Typography>
                <EventMap lat={lat} lng={lng} maxHeight="200px" mobileHeight="120px" borderRadius="16px" />
              </DataWrapper>
            </EventDetailRow>
            <EventDetailRow>
              <RowContainer>
                <TicketSolidIcon fill={theme.colors.defaultStrong} height={24} width={24} />
              </RowContainer>
              <DataWrapper>
                <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong} as="p">
                  {t('tickets.onSale', { count: ticketsArray.length })}
                </Typography>
                <TicketsList>
                  {ticketsArray.map(ticket => (
                    <TicketsItem key={ticket._id}>
                      <TicketNameBlock>
                        <Typography $variant="body-2-medium" color={theme.colors.defaultStrong} as="p">
                          {ticket.name}
                        </Typography>
                        <Typography $variant="body-2-medium" color={theme.colors.defaultWeak} as="span">
                          {ticket.type === TICKET_PAID ? t('tickets.paid', { price: ticket.price }) : t('tickets.free')}
                        </Typography>
                      </TicketNameBlock>
                      <Typography $variant="body-3-medium" color={theme.colors.defaultWeak} as="p">
                        {t('tickets.available', { available: ticket.availableSeats, capacity: ticket.capacity })}
                      </Typography>
                    </TicketsItem>
                  ))}
                </TicketsList>
              </DataWrapper>
            </EventDetailRow>
            <EventDetailRow>
              <RowContainer>
                <AttendeesIcon fill={theme.colors.defaultStrong} height={24} width={24} />
              </RowContainer>
              <DataWrapper>
                <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" color={theme.colors.defaultStrong} as="p">
                  {t('attendees.registered', { count: attendeesCount || 0 })}
                </Typography>
                <AttendeesList>
                  {Object.values(attendeesPerTicket).map(ticketInfo => (
                    <AttendeesPerTicket key={ticketInfo.name} >
                      <Typography $variant="body-2-medium" color={theme.colors.defaultStrong} as="p">
                        {ticketInfo.name}
                      </Typography>
                      <Typography $variant="body-3-medium" color={theme.colors.defaultWeak} as="p">
                        {t('attendees.registeredPerTicket', { count: ticketInfo.count })}
                      </Typography>
                    </AttendeesPerTicket>
                  ))}
                </AttendeesList>
              </DataWrapper>
            </EventDetailRow>

          </EventDetailsWrapper>
        </EventSummaryContainer>
      </Container>
    </ManageEventContainer>

  );
};

export default ManageEvent;

const AttendeesPerTicket = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding: ${({ theme }) => theme.sizing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }
`;

const TicketsList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
`;

const AttendeesList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
`;

const TicketsItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xs};
  width: 100%;
  padding: ${({ theme }) => theme.sizing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }
`;

const TicketNameBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
  justify-content: flex-start;
  flex-wrap: wrap;
  
`;

const ManageEventContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  padding: ${({ theme }) => theme.sizing.md};
  max-width: 1400px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const EventSummaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;
const EventCardWrapper = styled.div`
  display: flex;
  // padding: ${({ theme }) => theme.sizing.lg};
  padding: ${({ theme }) => theme.sizing.sm};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.sizing.sm};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultSubtle};
  flex-basis: calc(50% - 8px);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const EventCover = styled.img`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.sizing.xs};
    border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  }
`;

const EventDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  border-radius: ${({ theme }) => theme.sizing.sm};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};
  flex-basis: calc(50% - 8px);
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-radius: ${({ theme }) => theme.sizing.xs};
  }
`;

const DateContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: ${({ theme }) => theme.sizing.xxl};
  flex-direction: column;
  align-items: stretch;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.fill.defaultSubtle};
  overflow: hidden;
`;

const MonthDate = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ theme }) => theme.sizing.md};
  background: ${({ theme }) => theme.fill.defaultWeak};
`;

const DayDate = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
`;

const RowContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: ${({ theme }) => theme.sizing.xxl};
  height: 52px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};

  img {
    height: 24px;
  }
`;

const DataWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.sizing.xxxs};
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const EventDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  padding-bottom: 0px;
  width: 100%;

  &:last-child {
    padding-bottom: ${({ theme }) => theme.sizing.sm};
  }
`;

const EventDetailActions = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  // margin-top: ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: ${({ theme }) => theme.sizing.sm};
    border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  }
`;


const BannerWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  padding: ${({ theme }) => theme.sizing.md};
  padding-bottom: 0px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
    padding-bottom: 0px;
  }
`;

const BannerPublishContent = styled.div`
  background: ${({ theme }) => theme.fill.brandAlphaMain8};
  border-radius: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: ${({ theme }) => theme.sizing.xs};
  width: 100%;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-radius: ${({ theme }) => theme.radius.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xxs};
  }
`;

const BannerIconWrapper = styled.div`
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  background-color: ${({ theme }) => theme.fill.defaultAlphaMain16};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.lg} ;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border: none;
    padding: 0px;
    padding-left: ${({ theme }) => theme.sizing.xs};
    padding-top: ${({ theme }) => theme.sizing.xxs};
    // padding: 0px ${({ theme }) => theme.sizing.lg} ;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: ${({ theme }) => theme.sizing.xs};

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.xs} 0px; 
  flex-grow: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.sizing.xs};
  }
`;

const BannerData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xxs};
  flex-grow: 1;
`;

const BannerActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-right: ${({ theme }) => theme.sizing.sm};
  flex-shrink: 0;
`;
